import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Dialog, Field, Icon, Money, MoneyInput, StatusBadge, PersonDot, ConfirmDialog } from './ui.jsx';
import { parseMoney, parseCount, parsePercent, fmtPlain, fmt, r2 } from '../lib/money.js';
import { monthLabel, dateLabel } from '../lib/calendar.js';
import { buildModel, entryFor } from '../lib/model.js';
import { updateReview, confirmReview, reopenReview, resetReview } from '../lib/actions.js';
import { newId } from '../lib/state.js';

const toText = (v) => (v === null || v === undefined ? '' : fmtPlain(v));
const listToRows = (list) => (list || []).map((x) => ({ id: x.id || newId('i'), label: x.label || '', text: fmtPlain(x.amount) }));

// Lines like "Dinner out  45,00" that add up
function ItemList({ rows, setRows, addLabel, placeholder, disabled, allowNegative = true }) {
  const update = (id, patch) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  return (
    <div className="stack tight">
      {rows.map((r, i) => {
        const p = parseMoney(r.text, { allowEmpty: true, allowNegative });
        return (
          <div className="row" key={r.id}>
            <input className="input" style={{ flex: 2 }} aria-label={`${addLabel} ${i + 1}: what for`} placeholder={placeholder} value={r.label} disabled={disabled} onChange={(e) => update(r.id, { label: e.target.value })} />
            <div style={{ flex: 1, minWidth: 110 }}>
              <MoneyInput ariaLabel={`${addLabel} ${i + 1}: amount`} value={r.text} parsed={p} disabled={disabled} onChange={(t) => update(r.id, { text: t })} />
            </div>
            <button type="button" className="btn ghost icon" aria-label={`Remove ${addLabel.toLowerCase()} ${i + 1}`} disabled={disabled} onClick={() => setRows(rows.filter((x) => x.id !== r.id))}><Icon name="trash" /></button>
          </div>
        );
      })}
      {!disabled && <button type="button" className="linkbtn" style={{ alignSelf: 'flex-start' }} onClick={() => setRows([...rows, { id: newId('i'), label: '', text: '' }])}>+ {addLabel}</button>}
    </div>
  );
}

const rowsToItems = (rows, opts) => {
  const out = [];
  for (const r of rows) {
    const p = parseMoney(r.text, { allowEmpty: true, ...opts });
    if (!p.ok) return { error: p.error };
    if (p.value === null || p.value === 0) continue;
    out.push({ id: r.id, label: r.label.trim(), amount: p.value, ...(opts?.taxable !== undefined ? { taxable: opts.taxable } : {}) });
  }
  return { items: out };
};

export default function ReviewDialog({ month, personId, onClose }) {
  const { data, model, commit, today, sheets, me } = useApp();
  const entry0 = entryFor(model, month, personId);
  const stored = data.months[month]?.people?.[personId] || {};
  const confirmed = !!entry0?.confirmed;
  const fromSheet = entry0?.fromSheet || {};
  const [unlocked, setUnlocked] = useState(!confirmed);
  const locked = !unlocked;
  const isEmployee = entry0?.plan?.mode === 'employee';

  // What was typed (text, so half-typed values don't jump around)
  const [f, setF] = useState(() => ({
    sickDays: toText(stored.sickDays),
    absenceDays: toText(stored.absenceDays),
    vacationDays: toText(stored.vacationDays),
    commissionPct: toText(stored.commissionPct),
    actualNet: toText(stored.actualNet),
    actualMeal: toText(stored.actualMeal),
    reimbursed: toText(stored.reimbursed),
    refundActual: toText(stored.refundActual),
    note: stored.note || '',
  }));
  const [extraPay, setExtraPay] = useState(() => listToRows(stored.extraPay));
  const [extraIncome, setExtraIncome] = useState(() => listToRows(stored.extraIncome));
  const [extraSpending, setExtraSpending] = useState(() => listToRows(stored.extraSpending));
  const [askReset, setAskReset] = useState(false);
  const mealCounts = data.settings.countMealCard !== false;
  const [showReimbursed, setShowReimbursed] = useState(() => !!stored.reimbursed || entry0?.plan?.allowanceType === 'reimbursement');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: typeof v === 'string' ? v : v.target.value }));

  const parsed = useMemo(() => ({
    sickDays: parseCount(f.sickDays),
    absenceDays: parseCount(f.absenceDays),
    vacationDays: f.vacationDays.trim() === '' ? { ok: true, value: null } : parseCount(f.vacationDays),
    commissionPct: f.commissionPct.trim() === '' ? { ok: true, value: null } : parsePercent(f.commissionPct, { max: 1000 }),
    actualNet: parseMoney(f.actualNet, { allowEmpty: true, allowNegative: false }),
    actualMeal: parseMoney(f.actualMeal, { allowEmpty: true, allowNegative: false }),
    reimbursed: parseMoney(f.reimbursed, { allowEmpty: true, allowNegative: false }),
    refundActual: parseMoney(f.refundActual, { allowEmpty: true }),
  }), [f]);
  const lists = useMemo(() => ({
    extraPay: rowsToItems(extraPay, { taxable: true }),
    extraIncome: rowsToItems(extraIncome),
    extraSpending: rowsToItems(extraSpending),
  }), [extraPay, extraIncome, extraSpending]);
  const errors = [...Object.values(parsed).filter((p) => !p.ok).map((p) => p.error), ...Object.values(lists).filter((l) => l.error).map((l) => l.error)];
  const valid = errors.length === 0;

  const patch = useMemo(() => {
    if (!valid) return null;
    const p = {
      sickDays: parsed.sickDays.value || null,
      absenceDays: parsed.absenceDays.value || null,
      vacationDays: parsed.vacationDays.value,
      commissionPct: parsed.commissionPct.value,
      actualNet: parsed.actualNet.value,
      actualMeal: parsed.actualMeal.value,
      reimbursed: parsed.reimbursed.value || null,
      refundActual: parsed.refundActual.value,
      extraPay: lists.extraPay.items,
      extraIncome: lists.extraIncome.items,
      extraSpending: lists.extraSpending.items,
      note: f.note,
    };
    if (!isEmployee) { p.sickDays = null; p.absenceDays = null; p.vacationDays = null; p.commissionPct = null; }
    return p;
  }, [valid, parsed, lists, f.note, isEmployee]);

  // Live preview with what's typed
  const preview = useMemo(() => {
    if (!patch || locked) return entry0;
    try {
      const s = updateReview(data, month, personId, patch);
      return entryFor(buildModel(s, { today, sheets }), month, personId);
    } catch {
      return entry0;
    }
  }, [patch, locked, data, month, personId, today, sheets, entry0]);

  if (!entry0) {
    return (
      <Dialog title="Not available" onClose={onClose}>
        <p className="dim">This month isn't in the plan for this person any more.</p>
      </Dialog>
    );
  }

  const e = preview || entry0;
  const slip = e.slip;
  const label = `${e.name} · ${monthLabel(month)}`;
  const dirty = !locked && JSON.stringify(patch) !== JSON.stringify({
    sickDays: stored.sickDays || null, absenceDays: stored.absenceDays || null, vacationDays: stored.vacationDays ?? null,
    commissionPct: stored.commissionPct ?? null, actualNet: stored.actualNet ?? null, actualMeal: stored.actualMeal ?? null, reimbursed: stored.reimbursed || null,
    refundActual: stored.refundActual ?? null, extraPay: stored.extraPay || [], extraIncome: stored.extraIncome || [],
    extraSpending: stored.extraSpending || [], note: stored.note || '',
  });

  const save = (andConfirm) => {
    const res = commit(andConfirm ? `Confirm ${monthLabel(month)} for ${e.name}` : `Update ${monthLabel(month)} for ${e.name}`, (s) => {
      let next = confirmed ? reopenReview(s, month, personId) : s;
      next = updateReview(next, month, personId, patch);
      if (andConfirm) next = confirmReview(next, buildModel(next, { today, sheets }), month, personId, { by: me });
      return next;
    }, { undoToast: andConfirm });
    if (res.ok) onClose();
  };
  const reopen = () => {
    const res = commit(`Reopen ${monthLabel(month)} for ${e.name}`, (s) => reopenReview(s, month, personId));
    if (res.ok) setUnlocked(true);
  };
  const reset = () => {
    const res = commit(`Clear ${monthLabel(month)} for ${e.name}`, (s) => resetReview(s, month, personId), { undoToast: true });
    if (res.ok) onClose();
  };

  const dayHint = (v, unit = 'days') => (v ? `${v} ${unit}` : '');
  const net = e.expected.net;
  const assumedVacation = slip.days?.vacationAssumed ? slip.days.vacation : null;

  return (
    <Dialog
      title={<span className="row"><PersonDot color={e.color} />{label}</span>}
      onClose={onClose}
      wide
      dirty={dirty}
      footer={(
        <>
          {!locked && (stored && Object.keys(stored).length > 0) && <button type="button" className="btn ghost danger" onClick={() => setAskReset(true)}>Clear everything typed</button>}
          <span className="spacer" />
          {locked ? (
            <>
              <button type="button" className="btn" onClick={onClose}>Close</button>
              <button type="button" className="btn primary" onClick={reopen}><Icon name="edit" />Reopen to change</button>
            </>
          ) : (
            <>
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="button" className="btn" disabled={!valid} onClick={() => save(false)} title="Keep it waiting for review">Save for later</button>
              <button type="button" className="btn primary" disabled={!valid} onClick={() => save(true)}><Icon name="check" />{e.due ? 'Confirm' : 'Confirm early'}</button>
            </>
          )}
        </>
      )}
    >
      <div className="row wrap">
        <StatusBadge status={entry0.status} source={entry0.source} />
        <span className="dim small">Payday {dateLabel(e.payday)}{!e.due && ' (not yet)'}</span>
        {e.estimatedTax && <span className="badge estimate" title={`No ${month.slice(0, 4)} tax tables yet: using ${e.taxYear}'s`}>Tax figures estimated</span>}
        {e.jovem?.eligible && isEmployee && <span className="badge partial">IRS Jovem year {e.jovem.index} · {e.jovem.pct}% exempt</span>}
      </div>
      {locked && <div className="banner good"><Icon name="check" className="icon" /><div>Confirmed{stored.confirmedAt ? ` on ${new Date(stored.confirmedAt).toLocaleDateString()}` : ''}. It won't change if salaries or budgets are edited later. Reopen it to make corrections.</div></div>}
      {Object.keys(fromSheet).length > 0 && <div className="banner info"><Icon name="sheet" className="icon" /><div>Some values come from {e.name}'s linked spreadsheet and can only be changed there.</div></div>}

      <div className="grid2" style={{ alignItems: 'start' }}>
        <div className="stack">
          {isEmployee && (
            <section className="stack tight" aria-label="What happened this month">
              <h3 className="small dim" style={{ fontWeight: 650 }}>WHAT HAPPENED THIS MONTH</h3>
              <div className="grid3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
                <Field label="Sick days (unpaid)" error={!parsed.sickDays.ok ? parsed.sickDays.error : null} hint="Each costs a day's pay">
                  <input className={`input ${!parsed.sickDays.ok ? 'invalid' : ''}`} inputMode="decimal" value={f.sickDays} onChange={set('sickDays')} disabled={locked} placeholder="0" />
                </Field>
                <Field label="Other unpaid days" error={!parsed.absenceDays.ok ? parsed.absenceDays.error : null}>
                  <input className={`input ${!parsed.absenceDays.ok ? 'invalid' : ''}`} inputMode="decimal" value={f.absenceDays} onChange={set('absenceDays')} disabled={locked} placeholder="0" />
                </Field>
                <Field label="Holiday days taken" error={!parsed.vacationDays.ok ? parsed.vacationDays.error : null} hint={assumedVacation ? `${assumedVacation} assumed (usual holiday month)` : 'Only the meal card changes'}>
                  <input className={`input ${!parsed.vacationDays.ok ? 'invalid' : ''}`} inputMode="decimal" value={f.vacationDays} onChange={set('vacationDays')} disabled={locked} placeholder={assumedVacation ? String(assumedVacation) : '0'} />
                </Field>
                {e.plan.commission > 0 && (
                  <Field label="Commission, % of target" error={!parsed.commissionPct.ok ? parsed.commissionPct.error : null}>
                    <div className="input-wrap"><input className={`input money ${!parsed.commissionPct.ok ? 'invalid' : ''}`} inputMode="decimal" value={f.commissionPct} onChange={set('commissionPct')} disabled={locked} placeholder="100" /><span className="suffix">%</span></div>
                  </Field>
                )}
              </div>
              <Field label="Bonus, overtime or other pay on the payslip (taxed)" group>
                <ItemList rows={extraPay} setRows={setExtraPay} addLabel="Add extra pay" placeholder="e.g. Objectives bonus" disabled={locked} allowNegative={false} />
              </Field>
            </section>
          )}

          <section className="stack tight" aria-label="What actually arrived">
            <h3 className="small dim" style={{ fontWeight: 650 }}>WHAT ACTUALLY ARRIVED</h3>
            <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
              <Field label="Take-home pay received" hint={fromSheet.actualNet ? 'From spreadsheet' : `Expected ${fmt(net)}. Leave empty if it matches.`} error={!parsed.actualNet.ok ? parsed.actualNet.error : null}>
                <MoneyInput ariaLabel="Take-home pay received" value={fromSheet.actualNet ? fmtPlain(e.actual.net) : f.actualNet} parsed={parsed.actualNet} onChange={set('actualNet')} placeholder={fmtPlain(net)} disabled={locked || fromSheet.actualNet} autoFocus={!locked && e.due} />
              </Field>
              {showReimbursed && (
                <Field label="Expense reimbursements in it" hint={fromSheet.reimbursed ? 'From spreadsheet' : `Money back for expenses you paid yourself${e.plan?.allowanceType === 'reimbursement' && e.plan.allowance ? ` — ${(e.plan.allowanceLabel || 'Reimbursements').replace(/\s*\(.*\)$/, '')}, up to ${fmt(e.plan.allowance)} a month` : ''}. It's taken out: it isn't savings.`} error={!parsed.reimbursed.ok ? parsed.reimbursed.error : null}>
                  <MoneyInput ariaLabel="Expense reimbursements in it" value={fromSheet.reimbursed ? fmtPlain(e.actual.reimbursed) : f.reimbursed} parsed={parsed.reimbursed} onChange={set('reimbursed')} placeholder="0" disabled={locked || fromSheet.reimbursed} />
                </Field>
              )}
              {mealCounts && (e.expected.meal > 0 || stored.actualMeal !== undefined) && (
                <Field label="Meal card loaded" hint={fromSheet.actualMeal ? 'From spreadsheet' : `Expected ${fmt(e.expected.meal)}`} error={!parsed.actualMeal.ok ? parsed.actualMeal.error : null}>
                  <MoneyInput ariaLabel="Meal card loaded" value={fromSheet.actualMeal ? fmtPlain(e.actual.meal) : f.actualMeal} parsed={parsed.actualMeal} onChange={set('actualMeal')} placeholder={fmtPlain(e.expected.meal)} disabled={locked || fromSheet.actualMeal} />
                </Field>
              )}
              {(e.expected.refund !== 0 || stored.refundActual !== undefined) && (
                <Field label={`IRS settlement for ${Number(month.slice(0, 4)) - 1}`} hint={`Expected ${fmt(e.expected.refund, { sign: true })} (negative means paying)`} error={!parsed.refundActual.ok ? parsed.refundActual.error : null}>
                  <MoneyInput ariaLabel="IRS settlement" value={f.refundActual} parsed={parsed.refundActual} onChange={set('refundActual')} placeholder={fmtPlain(e.expected.refund)} disabled={locked} />
                </Field>
              )}
            </div>
            {!showReimbursed && !locked && <button type="button" className="linkbtn small" style={{ alignSelf: 'flex-start' }} onClick={() => setShowReimbursed(true)}>Part of it was money back for expenses?</button>}
            <Field label="Other money that came in (gifts, things sold, refunds…)" group>
              {fromSheet.extraIncome ? <span className="dim">{fmt(e.actual.extraIncome)} from spreadsheet</span>
                : <ItemList rows={extraIncome} setRows={setExtraIncome} addLabel="Add other income" placeholder="What was it?" disabled={locked} />}
            </Field>
          </section>

          <section className="stack tight" aria-label="Spending">
            <h3 className="small dim" style={{ fontWeight: 650 }}>SPENDING</h3>
            <p className="small muted">The budget below is already counted. Add only what went <b>beyond</b> it (negative if less was spent).</p>
            <Field label="Spent beyond the budget" group>
              {fromSheet.extraSpending ? <span className="dim">{fmt(e.actual.extraSpending)} from spreadsheet</span>
                : <ItemList rows={extraSpending} setRows={setExtraSpending} addLabel="Add extra spending" placeholder="e.g. Car repair" disabled={locked} />}
            </Field>
            <Field label="Notes">
              {fromSheet.note ? <span className="dim">{e.review.note}</span>
                : <textarea className="input" value={f.note} onChange={set('note')} disabled={locked} placeholder="Anything to remember about this month" maxLength={2000} />}
            </Field>
          </section>
        </div>

        <div className="stack">
          {isEmployee && slip.lines.length > 0 && (
            <section className="card" aria-label="Payslip">
              <div className="card-head"><h2>Payslip</h2><span className="spacer" /><span className="small muted">expected</span></div>
              <table className="plain">
                <tbody>
                  {slip.lines.map((l, i) => <tr key={i}><td className="left dim">{l.label}</td><td><Money value={l.amount} /></td></tr>)}
                  <tr className="total"><td className="left">Take-home (bank)</td><td><Money value={slip.netBank} /></td></tr>
                  {slip.mealCard > 0 && <tr className={mealCounts ? '' : 'muted'}><td className="left dim">Meal card ({slip.days.meal} days){mealCounts ? '' : ', not counted in savings'}</td><td><Money value={slip.mealCard} /></td></tr>}
                </tbody>
              </table>
              {slip.notes.length > 0 && <ul className="small muted" style={{ margin: '10px 0 0', paddingLeft: 18 }}>{slip.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
              {slip.withheld.length > 1 && <p className="small muted" style={{ marginTop: 8 }}>IRS is withheld separately on each bonus: {slip.withheld.map((w) => `${w.label} ${fmt(w.amount)}`).join(' · ')}</p>}
            </section>
          )}
          <section className="card" aria-label="To savings">
            <div className="card-head"><h2>To savings</h2></div>
            <div className="kv">
              <span>Take-home{e.actual.reimbursed > 0 ? ' (without reimbursements)' : ''}{mealCounts && e.actual.meal ? ' + meal card' : ''}{e.actual.refund ? ' + IRS refund' : ''}{e.actual.extraIncome ? ' + other income' : ''}</span><Money value={e.actual.income} />
              <span>Personal spending money</span><Money value={-e.expected.personal} />
              {e.expected.own > 0 && <><span>Own fixed expenses</span><Money value={-e.expected.own} /></>}
              <span>Share of joint expenses{e.expected.share !== undefined ? ` (${Math.round(e.expected.share * 100)}%)` : ''}</span><Money value={-e.expected.jointShare} />
              {e.actual.extraSpending !== 0 && <><span>Beyond budget</span><Money value={-e.actual.extraSpending} /></>}
              <span className="strong">This month</span><Money value={e.actual.contribution} className="strong" />
              <span>Expected</span><Money value={e.expected.contribution} />
              <span>Difference</span><Money value={r2(e.actual.contribution - e.expected.contribution)} sign color />
            </div>
            {e.actual.contribution < 0 && <p className="small" style={{ color: 'var(--warn)', marginTop: 10 }}>This month takes money out of savings.</p>}
          </section>
          {!valid && <div className="banner danger" role="alert"><Icon name="alert" className="icon" /><div>{errors[0]}</div></div>}
        </div>
      </div>
      {askReset && (
        <ConfirmDialog title="Clear this payday?" message="Everything typed for this payday goes back to the expected amounts. You can undo this." confirmLabel="Clear" danger onCancel={() => setAskReset(false)} onConfirm={reset} />
      )}
    </Dialog>
  );
}
